import { reactive } from "vue";
import { feedbackConfig, isConfigValid } from "./config";

// Tool-agnostic feedback facade. App code (header button, boot file) only ever
// talks to this object - never a vendor SDK directly - so a vendor swap is a
// one-file + one-env-key change.
//
// Adapter interface every provider adapter must satisfy:
//   init({ token, user, metadata, onReady }) -> Promise<void>
//        (load SDK, suppress default launcher, identify the user, call onReady)
//   open() -> void                (open the feedback modal)
//   teardown() -> void            (destroy widget + clear identity; safe to call twice)

const state = reactive({ ready: false });
let adapter = null;

async function loadAdapter() {
  switch (feedbackConfig.provider) {
    case "userback":
      return (await import("./adapters/userback")).default;
    default:
      return null;
  }
}

export const feedbackWidget = {
  state,

  async init(user) {
    if (!isConfigValid() || state.ready) return;
    try {
      adapter = adapter || (await loadAdapter());
      if (!adapter) return;
      await adapter.init({
        token: feedbackConfig.token,
        user: {
          id: String(user.id),
          email: user.email,
          name: [user.first_name, user.last_name].filter(Boolean).join(" "),
        },
        metadata: {
          // Company entity lives at user.company (id/name); user.company_role is a
          // role-within-company, not the company name — see src/boot/embeds.js.
          company: user.company ? user.company.name : null,
          role: user.role ? user.role.code : null,
          app: "greenlists-customer",
        },
        onReady: () => {
          state.ready = true;
        },
      });
    } catch (e) {
      console.error("[feedback] init failed", e);
      state.ready = false;
    }
  },

  open() {
    if (!state.ready || !adapter) return;
    try {
      adapter.open();
    } catch (e) {
      console.error("[feedback] open failed", e);
    }
  },

  teardown() {
    if (!adapter) return;
    try {
      adapter.teardown();
    } catch (e) {
      /* ignore */
    }
    state.ready = false;
  },

  getMaskClasses() {
    return { mask: ["capture-mask"], block: ["capture-block"] };
  },
};
